import { Injectable } from '@angular/core';
import { CollectionFilterRequest } from '../requests/collection-filter.request';
import {
  isArray,
  isBoolean,
  isEmptyArray,
  isEmptyString,
  isNil,
  isString,
  Nillable,
} from '@web/utils';
import { TableFilter } from '../../collection/types/table-filter';
import { FromTableCollectionFilterRequestBuilder } from './from-table-collection-filter-request.builder';
import { TextCollectionFilterRequestBuilder } from './text-collection-filter-request.builder';
import { DateCollectionFilterRequestBuilder } from './date-collection-filter-request.builder';
import { BooleanCollectionFilterRequestBuilder } from './boolean-collection-filter-request.builder';

@Injectable({
  providedIn: 'root',
})
export class CollectionFilterRequestBuilder<Filter = never> {
  private _requests: CollectionFilterRequest[] = [];

  public reset(): this {
    this._requests = [];

    return this;
  }

  public newInstance<Filter>(): CollectionFilterRequestBuilder<Filter> {
    return new CollectionFilterRequestBuilder<Filter>();
  }

  public fromTableFilter<Filter>(
    filter: Nillable<TableFilter<Filter>>,
  ): FromTableCollectionFilterRequestBuilder<Filter> {
    return new FromTableCollectionFilterRequestBuilder(filter);
  }

  public appendText(
    factory: (
      builder: TextCollectionFilterRequestBuilder<Filter>,
    ) => Nillable<CollectionFilterRequest>,
  ): this {
    const result = factory(new TextCollectionFilterRequestBuilder<Filter>());

    if (isNil(result)) {
      return this;
    }

    this._requests.push(result);

    return this;
  }

  public appendBoolean(
    factory: (
      builder: BooleanCollectionFilterRequestBuilder<Filter>,
    ) => Nillable<CollectionFilterRequest>,
  ): this {
    const result = factory(new BooleanCollectionFilterRequestBuilder<Filter>());

    if (isNil(result)) {
      return this;
    }

    this._requests.push(result);

    return this;
  }

  public appendDate(
    factory: (
      builder: DateCollectionFilterRequestBuilder<Filter>,
    ) => Nillable<CollectionFilterRequest>,
  ): this {
    const result = factory(new DateCollectionFilterRequestBuilder<Filter>());

    if (isNil(result)) {
      return this;
    }

    this._requests.push(result);

    return this;
  }

  public build(): CollectionFilterRequest[] {
    const result = [...this._requests.filter(this.byValidValue())];

    this.reset();

    return result;
  }

  private byValidValue(): (request: CollectionFilterRequest) => boolean {
    return (request) => {
      const { value } = request;

      if (isNil(value)) {
        return false;
      }

      if (isBoolean(value)) {
        return true;
      }

      if (isString(value)) {
        return !isEmptyString(value);
      }

      if (isArray(value)) {
        return !isEmptyArray(value as []);
      }

      return true;
    };
  }
}
